<!-- One Chat Floating Widget - refactored Vue template -->
<div
  v-if="is_available"
  class="floating-chat-widget"
  :class="{ open: is_open, pulsing: launcher_pulsing }"
>
  <transition name="fc-toast">
    <div v-if="toast" class="fc-toast shadow d-flex gap-2 align-items-start" :class="`is-${toast.tone}`">
      <i class="fas" :class="toast.tone === 'error' ? 'fa-circle-exclamation' : 'fa-circle-check'"></i>
      <div class="min-w-0">
        <strong class="d-block">{{ toast.title }}</strong>
        <span class="d-block text-truncate">{{ toast.text }}</span>
      </div>
    </div>
  </transition>

  <transition name="fc-backdrop">
    <div
      v-if="is_open"
      class="floating-chat-backdrop"
      aria-hidden="true"
      @click="collapse_chat"
    ></div>
  </transition>

  <transition name="fc-panel">
    <section
      v-if="is_open"
      class="floating-chat-panel onechat-floating-shell d-flex flex-column shadow-lg"
      :class="{ 'is-chat-open': active_conversation, 'is-conversation-list': !active_conversation }"
      aria-label="AIC One Chat floating widget"
      :style="panel_style"
      @touchstart.passive="start_mobile_drag"
      @touchmove.passive="move_mobile_drag"
      @touchend="end_mobile_drag"
    >
      <div class="of-mobile-grabber" aria-hidden="true"></div>
      <div v-if="!active_conversation" class="of-command-bar d-flex align-items-center gap-2 flex-shrink-0">
        <div class="of-command-search d-flex align-items-center flex-grow-1">
          <i class="fas fa-search"></i>
          <input v-model="search_query" type="search" placeholder="Search chats, files, and members" aria-label="Search chats">
        </div>

        <button type="button" class="of-icon-btn btn btn-light rounded-circle d-inline-flex align-items-center justify-content-center p-0" title="Minimize chat" @click="collapse_chat">
          <i class="fas fa-close"></i>
        </button>

      </div>

      <div class="of-body d-flex min-h-0 flex-grow-1">
        <aside class="of-sidebar d-flex flex-column flex-shrink-0">
          <div class="of-sidebar-header d-flex align-items-center justify-content-between">
            <div>
              <h6 class="mb-0">One Chat</h6>
            </div>
            <button type="button" class="of-add-btn btn btn-danger rounded-circle d-inline-flex align-items-center justify-content-center p-0" title="Start new chat" @click="new_chat_open = true">
              <i class="fas fa-plus"></i>
            </button>
          </div>

          <div class="of-section-tabs d-grid px-2 pb-2">
            <button type="button" class="d-flex align-items-center justify-content-center" :class="{ active: active_section === 'recent' }" @click="select_section('recent')">
              <i class="fas fa-clock"></i>
              <span>Recent</span>
              <b class="d-inline-flex align-items-center justify-content-center">{{ conversations.length }}</b>
            </button>
            <button type="button" class="d-flex align-items-center justify-content-center" :class="{ active: active_section === 'direct' }" @click="select_section('direct')">
              <i class="fas fa-user"></i>
              <span>DMs</span>
              <b class="d-inline-flex align-items-center justify-content-center">{{ direct_count }}</b>
            </button>
            <button type="button" class="d-flex align-items-center justify-content-center" :class="{ active: active_section === 'spaces' }" @click="select_section('spaces')">
              <i class="fas fa-users"></i>
              <span>Spaces</span>
              <b class="d-inline-flex align-items-center justify-content-center">{{ space_count }}</b>
            </button>
          </div>

          <div class="of-conversations flex-grow-1 overflow-auto">
            <div v-if="conversations_loading" class="of-empty-state h-100 d-flex flex-column align-items-center justify-content-center text-center">
              <span class="spinner-border text-danger"></span>
              <p class="mt-2 mb-0">Loading chats...</p>
            </div>

            <div v-else-if="!filtered_conversations.length" class="of-empty-state h-100 d-flex flex-column align-items-center justify-content-center text-center p-3">
              <div class="of-empty-icon bg-danger-subtle text-danger d-inline-flex align-items-center justify-content-center mb-3"><i class="fas fa-comments"></i></div>
              <h6>{{ active_section === 'spaces' ? 'No spaces found' : 'No chats found' }}</h6>
              <p>Try another search or start a new conversation.</p>
              <button v-if="active_section === 'spaces'" type="button" class="btn btn-sm btn-danger" @click="new_conversation_mode = 'group'; new_chat_open = true">Create Group</button>
              <button v-else type="button" class="btn btn-sm btn-danger" @click="new_chat_open = true">New Chat</button>
            </div>

            <button
              v-for="conversation in filtered_conversations"
              v-else
              :key="conversation.id"
              type="button"
              class="of-conversation d-flex align-items-center gap-2 w-100"
              :class="{ active: String(active_conversation_id) === String(conversation.id), unread: conversation.unread_count > 0 }"
              @click="select_conversation(conversation)"
              @contextmenu.prevent="open_context_menu($event, conversation, 'list')"
            >
              <span class="of-avatar d-inline-flex align-items-center justify-content-center flex-shrink-0" :class="{ space: is_space_conversation(conversation) }">
                <i v-if="is_space_conversation(conversation)" class="fas fa-users"></i>
                <span v-else>{{ get_initials(get_conversation_name(conversation)) }}</span>
              </span>
              <span class="of-conversation-copy text-start min-w-0 flex-grow-1">
                <strong class="d-block text-truncate">{{ get_conversation_name(conversation) }}</strong>
                <small class="d-block text-truncate">{{ get_conversation_subtitle(conversation) }}</small>
              </span>
              <span class="of-conversation-meta text-end flex-shrink-0">
                <small>{{ format_time(conversation.updated_at || conversation.last_message?.created_at) }}</small>
                <b v-if="conversation.unread_count" class="of-unread-count d-inline-flex align-items-center justify-content-center">{{ conversation.unread_count > 99 ? '99+' : conversation.unread_count }}</b>
              </span>
              <span class="of-quick-actions d-flex gap-2">
                <button type="button" class="d-inline-flex align-items-center justify-content-center" title="Chat options" @click.stop="open_context_menu($event, conversation, 'list')"><i class="fas fa-ellipsis-h"></i></button>
              </span>
            </button>
          </div>
        </aside>

        <section class="of-chat d-flex flex-column flex-grow-1 min-w-0">
          <template v-if="active_conversation">
            <div class="of-chat-header d-flex align-items-center justify-content-between flex-shrink-0">
              <div class="d-flex align-items-center gap-2 min-w-0">
                <button type="button" class="of-widget-back-btn btn btn-light rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 p-0" title="Back to chats" @click="active_conversation_id = null">
                  <i class="fas fa-arrow-left"></i>
                </button>
                <span class="of-avatar of-avatar-lg d-inline-flex align-items-center justify-content-center flex-shrink-0" :class="{ space: is_space_conversation(active_conversation) }">
                  <i v-if="is_space_conversation(active_conversation)" class="fas fa-users"></i>
                  <span v-else>{{ get_initials(get_conversation_name(active_conversation)) }}</span>
                </span>
                <div class="min-w-0 text-start">
                  <strong class="d-block text-truncate">{{ get_conversation_name(active_conversation) }}</strong>
                </div>
              </div>
              <div class="d-flex align-items-center gap-1">
                <button v-if="active_is_group" type="button" class="of-icon-btn subtle btn btn-light rounded-circle d-inline-flex align-items-center justify-content-center p-0" title="Add members" :disabled="!active_group_can_manage" @click="add_member_open = true">
                  <i class="fas fa-user-plus"></i>
                </button>
                <button type="button" class="of-icon-btn subtle btn btn-light rounded-circle d-inline-flex align-items-center justify-content-center p-0" title="Conversation info" @click="open_conversation_details">
                  <i class="fas fa-circle-info"></i>
                </button>
                <button type="button" class="of-icon-btn subtle btn btn-light rounded-circle d-inline-flex align-items-center justify-content-center p-0" title="Search this chat" @click="open_search_overlay">
                  <i class="fas fa-search"></i>
                </button>
                <button type="button" class="of-icon-btn subtle btn btn-light rounded-circle d-inline-flex align-items-center justify-content-center p-0" title="Conversation options" @click.stop="open_context_menu($event, active_conversation, 'header')">
                  <i class="fas fa-ellipsis-h"></i>
                </button>
              </div>
            </div>

            <div v-if="pinned_messages.length" class="of-pinned-bar d-flex align-items-center gap-2">
              <i class="fas fa-thumbtack"></i>
              <span class="text-truncate">{{ pinned_messages.length }} pinned {{ pinned_messages.length === 1 ? 'message' : 'messages' }}</span>
            </div>

            <div ref="message_scroller" class="of-messages flex-grow-1 overflow-auto" @scroll.passive="on_message_scroll">
              <div v-if="messages_loading" class="of-empty-state h-100 d-flex flex-column align-items-center justify-content-center">
                <span class="spinner-border text-danger"></span>
                <p class="mt-2 mb-0">Loading messages...</p>
              </div>

              <div v-else-if="!messages.length" class="of-empty-state h-100 d-flex flex-column align-items-center justify-content-center text-center">
                <div class="of-empty-icon bg-danger-subtle text-danger d-inline-flex align-items-center justify-content-center mb-3"><i class="fas fa-comment-dots"></i></div>
                <h6>No messages yet</h6>
                <p>Send the first message in this AIC One Chat conversation.</p>
              </div>

              <div v-if="messages_has_more && !messages_loading" class="text-center mb-2">
                <button type="button" class="btn btn-sm btn-light" :disabled="loading_more_messages" @click="load_more_messages">
                  <i v-if="loading_more_messages" class="fas fa-spinner fa-spin me-1"></i>
                  Older messages
                </button>
              </div>

              <article
                v-for="message in messages"
                :key="message.id"
                class="of-message-row d-flex"
                :class="{ sent: is_my_message(message) }"
                :data-message-id="message.id"
                @dblclick="react_to_message(message, quick_emojis[1])"
              >
                <span v-if="!is_my_message(message)" class="of-avatar of-avatar-sm d-inline-flex align-items-center justify-content-center flex-shrink-0">
                  {{ get_initials(get_user_name(message.sender)) }}
                </span>
                <div class="of-message-stack">
                  <div v-if="message.reply_to" class="of-reply-preview">Replying to: {{ message.reply_to.content }}</div>
                  <div class="of-message-bubble">
                    <div v-if="String(editing_message_id) === String(message.id)" class="of-edit-area w-100">
                      <textarea
                        ref="edit_message_input"
                        v-model="edit_draft"
                        class="form-control w-100"
                        rows="3"
                        @keydown.enter.prevent="submit_edit(message)"
                        @keydown.esc.prevent="cancel_edit"
                      ></textarea>
                      <div class="d-flex justify-content-end flex-wrap gap-2 mt-2">
                        <button type="button" class="btn btn-sm btn-light" @click="cancel_edit">Cancel</button>
                        <button type="button" class="btn btn-sm btn-danger" :disabled="saving_edit || !edit_draft.trim()" @click="submit_edit(message)">
                          <i v-if="saving_edit" class="fas fa-spinner fa-spin me-1"></i>Save
                        </button>
                      </div>
                    </div>
                    <p v-else-if="message.content" class="mb-1">{{ message.content }}</p>
                    <div v-if="message.attachments && message.attachments.length" class="of-attachments d-flex flex-column gap-2 mb-1">
                      <div v-for="file in message.attachments" :key="file.id || get_chat_file_name(file)">
                        <div
                          v-if="is_image_file(get_chat_file_name(file))"
                          class="of-chat-img-wrapper position-relative d-inline-block"
                          role="button"
                          @click.stop="preview_chat_file(file)"
                        >
                          <img
                            :src="get_chat_file_url(file)"
                            :alt="get_chat_file_name(file)"
                            class="of-chat-inline-img"
                            loading="lazy"
                            @load="scroll_to_bottom()"
                          >
                          <div class="of-chat-img-overlay position-absolute d-flex align-items-center justify-content-center">
                            <i class="fas fa-expand-alt"></i>
                          </div>
                        </div>

                        <video
                          v-else-if="is_video_file(get_chat_file_name(file))"
                          :src="get_chat_file_url(file)"
                          class="of-chat-inline-video"
                          controls
                          preload="metadata"
                          playsinline
                          @play="stop_other_media($event)"
                        ></video>

                        <div v-else-if="is_audio_file(get_chat_file_name(file))" class="of-chat-audio-wrapper">
                          <div class="of-chat-audio-header d-flex align-items-center gap-2 mb-2">
                            <div class="of-chat-audio-icon d-inline-flex align-items-center justify-content-center"><i class="fas fa-music"></i></div>
                            <span class="of-chat-audio-name text-truncate">{{ get_chat_file_name(file) }}</span>
                          </div>
                          <audio
                            :src="get_chat_file_url(file)"
                            class="of-chat-inline-audio w-100"
                            controls
                            preload="metadata"
                            @play="stop_other_media($event)"
                          ></audio>
                        </div>

                        <div v-else class="of-attach-card d-flex align-items-center" role="button" @click.stop="preview_chat_file(file)">
                          <div class="of-attach-icon d-inline-flex align-items-center justify-content-center"><i :class="file_icon_chat(get_chat_file_name(file))"></i></div>
                          <div class="flex-grow-1 overflow-hidden">
                            <div class="of-attach-name fw-semibold text-truncate">{{ get_chat_file_name(file) }}</div>
                            <div class="of-attach-hint">Tap to preview</div>
                          </div>
                          <a
                            :href="get_chat_file_url(file)"
                            :download="get_chat_file_name(file)"
                            class="of-attach-dl-btn d-inline-flex align-items-center justify-content-center"
                            title="Download"
                            @click.stop
                          >
                            <i class="fas fa-download"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                    <div class="of-message-meta">
                      <span v-if="formatted_reactions(message)" class="of-reaction-pill d-inline-flex">{{ formatted_reactions(message) }}</span>
                      <time>{{ format_time(message.created_at) }}</time>
                      <span v-if="is_my_message(message)" class="of-read-receipt"><i class="fas fa-check-double"></i></span>
                    </div>
                  </div>
                  <div v-if="String(editing_message_id) !== String(message.id)" class="of-message-actions d-flex align-items-center shadow-sm" :class="{ sent: is_my_message(message) }" @click.stop>
                    <button
                      v-for="emoji in quick_emojis"
                      :key="`${message.id}-${emoji}`"
                      type="button"
                      class="of-quick-emoji"
                      :class="{ active: my_reaction_for(message) === emoji }"
                      :title="emoji"
                      @click="react_to_message(message, emoji, $event)"
                    >{{ emoji }}</button>
                    <button type="button" title="More reactions" @click="react_to_message(message, '\u{1F642}', $event)"><i class="far fa-smile"></i></button>
                    <span class="of-message-divider"></span>
                    <button v-if="is_my_message(message)" type="button" title="Edit" @click="edit_message(message)"><i class="fas fa-pencil-alt"></i></button>
                    <button type="button" class="of-more-action" title="More actions" @click="toggle_message_menu(message, $event)">
                      <i class="fas fa-ellipsis-h"></i>
                    </button>
                  </div>
                  <div
                    v-if="String(editing_message_id) !== String(message.id) && String(active_message_menu_id) === String(message.id)"
                    class="of-message-more-menu shadow"
                    :class="{ up: get_message_menu_direction(message) === 'up', inside: get_message_menu_direction(message) === 'inside', sent: is_my_message(message) }"
                    @click.stop
                  >
                    <button type="button" class="of-message-more-item btn btn-link text-decoration-none d-flex align-items-center" @click="set_reply(message); active_message_menu_id = null">
                      <i class="fas fa-reply fa-fw"></i>
                      <span>Reply</span>
                    </button>
                    <button type="button" class="of-message-more-item btn btn-link text-decoration-none d-flex align-items-center" @click="pin_message(message)">
                      <i class="fas fa-thumbtack fa-fw"></i>
                      <span>{{ message.is_pinned ? 'Unpin message' : 'Pin message' }}</span>
                    </button>
                    <div class="of-message-more-divider"></div>
                    <button type="button" class="of-message-more-item danger btn btn-link text-decoration-none d-flex align-items-center" @click="confirm_delete_message(message, 'me')">
                      <i class="fas fa-trash fa-fw"></i>
                      <span>Delete for me</span>
                    </button>
                    <button v-if="is_my_message(message)" type="button" class="of-message-more-item danger btn btn-link text-decoration-none d-flex align-items-center" @click="confirm_delete_message(message, 'everyone')">
                      <i class="fas fa-trash-alt fa-fw"></i>
                      <span>Delete for everyone</span>
                    </button>
                  </div>
                </div>
              </article>

            </div>

            <div v-if="new_messages_banner || user_scrolled_up" class="of-jump-latest d-flex justify-content-center">
              <button type="button" class="new-messages-banner d-inline-flex align-items-center gap-2" @click="jump_to_bottom">
                <i class="fas fa-arrow-down"></i>
                <span>{{ new_messages_banner ? 'New messages' : 'Jump to latest' }}</span>
              </button>
            </div>

            <div v-if="selected_file" class="of-file-strip d-flex align-items-center gap-2">
              <div v-if="selected_file_preview_url" class="of-file-thumb"><img :src="selected_file_preview_url" alt="preview"></div>
              <i v-else class="fas fa-paperclip"></i>
              <span class="text-truncate flex-grow-1">{{ selected_file.name }}</span>
              <button type="button" class="of-file-remove d-inline-flex align-items-center justify-content-center" @click="remove_selected_file"><i class="fas fa-xmark"></i></button>
            </div>

            <div v-if="reply_to" class="of-reply-bar d-flex align-items-center justify-content-between gap-2">
              <span class="text-truncate">Replying to {{ get_user_name(reply_to.sender) }}: {{ reply_to.content }}</span>
              <button type="button" class="btn btn-sm btn-link p-0" @click="clear_reply">Clear</button>
            </div>

            <form class="of-composer d-flex align-items-center gap-2" @submit.prevent="send_message">
              <input ref="file_input" type="file" class="d-none" @change="handle_file_select">
              <button type="button" class="of-tool-btn" title="Attach file" @click="open_attachment">
                <i class="fas fa-paperclip"></i>
              </button>
              <input
                ref="composer_input"
                v-model="draft"
                class="form-control flex-grow-1 min-w-0"
                :class="{ 'is-invalid': draft_error }"
                type="text"
                placeholder="Send a message..."
                @input="draft_error = ''"
                @keydown.enter.prevent="send_message"
              >
              <button type="submit" class="of-send-btn btn btn-danger rounded-circle d-inline-flex align-items-center justify-content-center p-0" title="Send message" :disabled="sending || (!draft.trim() && !selected_file)">
                <span v-if="sending" class="spinner-border spinner-border-sm"></span>
                <i v-else class="fas fa-paper-plane"></i>
              </button>
            </form>
            <div v-if="draft_error" class="of-composer-error">{{ draft_error }}</div>
          </template>
        </section>
      </div>

      <aside v-if="conversation_details_open" class="of-drawer shadow-lg d-flex flex-column">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h6 class="mb-0">One Chat Info</h6>
          <button type="button" class="of-icon-btn subtle btn btn-light rounded-circle d-inline-flex align-items-center justify-content-center p-0" @click="conversation_details_open = false"><i class="fas fa-xmark"></i></button>
        </div>
        <div v-if="active_conversation" class="of-detail-card mb-3">
          <div class="d-flex align-items-center gap-3 text-start">
            <span class="of-avatar of-avatar-lg d-inline-flex align-items-center justify-content-center flex-shrink-0" :class="{ space: is_space_conversation(active_conversation) }">
              <i v-if="is_space_conversation(active_conversation)" class="fas fa-users"></i>
              <span v-else>{{ get_initials(get_conversation_name(active_conversation)) }}</span>
            </span>
            <div class="min-w-0">
              <strong class="d-block text-truncate">{{ get_conversation_name(active_conversation) }}</strong>
              <small class="d-block text-truncate">{{ active_is_group ? `${active_conversation_members.length} members` : get_conversation_subtitle(active_conversation) }}</small>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center justify-content-between mb-2">
          <strong>Members</strong>
          <span class="badge rounded-pill text-bg-light">{{ active_conversation_members.length }}</span>
        </div>
        <div class="overflow-auto flex-grow-1">
          <div
            v-for="member in active_conversation_members"
            :key="member.id"
            class="of-member-row d-flex align-items-center gap-2"
          >
            <span class="of-avatar of-avatar-sm d-inline-flex align-items-center justify-content-center">{{ member.initials || get_initials(get_user_name(member)) }}</span>
            <span class="min-w-0">
              <strong class="d-block text-truncate">{{ get_user_name(member) }}</strong>
              <small>{{ member.role || member.username || 'Authorized user' }}</small>
            </span>
          </div>
        </div>
        <button v-if="active_is_group" type="button" class="btn btn-danger w-100 mt-3" :disabled="!active_group_can_manage" @click="add_member_open = true">Add Member</button>
      </aside>

      <div v-if="search_overlay_open" class="of-overlay d-flex" @click.self="search_overlay_open = false">
        <div class="of-search-modal shadow-lg">
          <div class="of-search-modal-header d-flex align-items-center justify-content-between">
            <div>
              <h6 class="mb-0">Find in chat</h6>
              <small>Search this conversation</small>
            </div>
            <button type="button" class="of-icon-btn subtle btn btn-light rounded-circle d-inline-flex align-items-center justify-content-center p-0" @click="search_overlay_open = false"><i class="fas fa-xmark"></i></button>
          </div>
          <div class="of-command-search of-chat-search-input d-flex align-items-center">
            <i class="fas fa-search"></i>
            <input ref="global_search_input" v-model="global_search_query" type="search" placeholder="Search messages in this chat">
          </div>
          <div v-if="!global_search_query.trim()" class="of-empty-state text-center p-3">
            <div class="of-empty-icon d-inline-flex align-items-center justify-content-center mx-auto mb-2"><i class="fas fa-magnifying-glass"></i></div>
            <h6>Find messages</h6>
            <p>Search by message text, sender, or attachment name.</p>
          </div>
          <div v-else-if="!message_search_results.length" class="of-empty-state text-center p-3">
            <div class="of-empty-icon d-inline-flex align-items-center justify-content-center mx-auto mb-2"><i class="fas fa-magnifying-glass"></i></div>
            <h6>No messages found</h6>
            <p>Try another search term.</p>
          </div>
          <button v-for="message in message_search_results" :key="`message-search-${message.id}`" type="button" class="of-search-result d-flex align-items-start gap-2 w-100 text-start" @click="jump_to_message(message)">
            <span class="of-avatar of-avatar-sm d-inline-flex align-items-center justify-content-center flex-shrink-0">{{ get_initials(get_user_name(message.sender)) }}</span>
            <span class="min-w-0 flex-grow-1">
              <strong class="d-block text-truncate">{{ get_user_name(message.sender) }}</strong>
              <small class="d-block text-truncate">{{ get_message_search_preview(message) }}</small>
            </span>
            <time class="flex-shrink-0">{{ format_time(message.created_at) }}</time>
          </button>
        </div>
      </div>
    </section>
  </transition>

  <transition name="fc-modal">
    <div v-if="new_chat_open" class="of-modal-backdrop of-compose-backdrop d-flex" @click.self="close_new_chat_modal">
      <div class="of-modal of-new-conv-modal shadow-lg">
        <div class="of-modal-header of-compose-header d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center gap-2 min-w-0">
            <span class="of-compose-header-icon d-inline-flex align-items-center justify-content-center"><i class="fas fa-comment-dots"></i></span>
            <span class="min-w-0">
              <h5 class="of-modal-title mb-0">{{ new_conversation_mode === 'group' ? 'Create group' : 'Start conversation' }}</h5>
              <small class="of-compose-header-subtitle">
                {{ new_conversation_mode === 'group' ? 'Select members for this chat' : 'Choose someone to message' }}
              </small>
            </span>
          </div>
          <button type="button" class="of-icon-btn subtle btn btn-light rounded-circle d-inline-flex align-items-center justify-content-center p-0" @click="close_new_chat_modal"><i class="fas fa-xmark"></i></button>
        </div>

        <div class="of-modal-body">
          <div class="of-compose-mode d-grid">
            <button
              type="button"
              class="of-compose-mode-btn btn text-start d-inline-flex align-items-center justify-content-start gap-2"
              :class="{ active: new_conversation_mode === 'direct' }"
              @click="new_conversation_mode = 'direct'"
            >
              <span class="of-compose-icon d-inline-flex align-items-center justify-content-center"><i class="fas fa-user"></i></span>
              <span class="of-compose-copy d-flex flex-column">
                <strong>Direct</strong>
                <small>One person</small>
              </span>
            </button>
            <button
              type="button"
              class="of-compose-mode-btn btn text-start d-inline-flex align-items-center justify-content-start gap-2"
              :class="{ active: new_conversation_mode === 'group' }"
              @click="new_conversation_mode = 'group'"
            >
              <span class="of-compose-icon d-inline-flex align-items-center justify-content-center"><i class="fas fa-users"></i></span>
              <span class="of-compose-copy d-flex flex-column">
                <strong>Group</strong>
                <small>Team chat</small>
              </span>
            </button>
          </div>

          <div v-if="new_conversation_mode === 'group'" class="of-group-panel">
            <div class="of-group-title-row d-flex align-items-center gap-2">
              <div class="of-group-badge d-flex align-items-center justify-content-center">
                <i class="fas fa-layer-group"></i>
              </div>
              <div class="min-w-0 flex-grow-1">
                <label class="of-group-label mb-0">Group name</label>
              </div>
              <div class="of-group-count flex-shrink-0">{{ group_modal_member_count }} selected</div>
            </div>
            <textarea
              v-model="new_group_name"
              class="of-group-name-input"
              maxlength="80"
              rows="1"
              placeholder="e.g. Finance review"
            ></textarea>
            <div v-if="selected_group_users.length" class="of-selected-strip d-flex flex-wrap align-items-start">
              <button
                v-for="user in selected_group_users"
                :key="`selected-${user.id}`"
                type="button"
                class="of-selected-pill d-inline-flex align-items-center"
                @click="toggle_group_user(user.id)"
              >
                <span class="d-inline-flex align-items-center justify-content-center">{{ get_initials(get_user_name(user)) }}</span>
                {{ get_user_name(user) }}
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <div class="of-modal-search" :class="{ 'group-search': new_conversation_mode === 'group' }">
            <div class="of-contact-heading d-flex align-items-center justify-content-between">
              <span>{{ new_conversation_mode === 'group' ? 'Add members' : 'Choose a contact' }}</span>
              <small v-if="filtered_users.length">{{ filtered_users.length }} shown</small>
            </div>
            <div class="of-modal-search-inner d-flex align-items-center gap-2">
              <i class="fas fa-search flex-shrink-0"></i>
              <input
                v-model="user_search"
                type="text"
                class="flex-grow-1"
                placeholder="Search by name or email"
                :disabled="users_loading"
              >
            </div>
          </div>

          <div v-if="users_loading" class="of-empty-state d-flex flex-column align-items-center justify-content-center py-5">
            <span class="spinner-border text-danger"></span>
            <p class="mt-2 mb-0">Loading contacts...</p>
          </div>
          <div v-else-if="filtered_users.length === 0" class="of-empty-state d-flex flex-column align-items-center justify-content-center text-center py-5">
            <div class="of-empty-icon bg-danger-subtle text-danger d-inline-flex align-items-center justify-content-center mb-3"><i class="fas fa-users"></i></div>
            <h6>{{ user_search ? 'No contacts found' : 'No contacts available' }}</h6>
            <p v-if="user_search">Try a different search term.</p>
          </div>
          <div v-else class="of-contact-list">
            <button
              v-for="user in filtered_users"
              :key="user.id"
              type="button"
              class="of-contact-item d-flex align-items-center gap-3 w-100 text-start"
              :class="{ active: new_conversation_mode === 'group' ? is_group_user_selected(user.id) : String(new_conversation_user_id) === String(user.id) }"
              :disabled="new_conversation_mode === 'group' && String(user.id) === String(current_user_id)"
              @click="select_modal_user(user)"
            >
              <span class="of-avatar of-avatar-md d-inline-flex align-items-center justify-content-center flex-shrink-0">
                {{ get_initials(get_user_name(user)) }}
              </span>
              <span class="flex-grow-1 overflow-hidden">
                <span class="of-contact-name d-block">
                  {{ get_user_name(user) }}
                  <span v-if="String(user.id) === String(current_user_id)" class="text-muted fw-normal">(Me)</span>
                </span>
                <span class="of-contact-email d-block text-truncate">{{ user.email }}</span>
              </span>
              <span
                class="of-contact-select flex-shrink-0 d-flex align-items-center justify-content-center"
                :class="{ checked: new_conversation_mode === 'group' ? is_group_user_selected(user.id) : String(new_conversation_user_id) === String(user.id) }"
              >
                <i class="fas fa-check"></i>
              </span>
            </button>
          </div>
        </div>

        <div class="of-modal-footer d-flex align-items-center gap-2">
          <div v-if="new_conversation_mode === 'group'" class="of-footer-summary me-auto">
            {{ new_group_name.trim() || 'Untitled group' }} &middot; {{ group_modal_member_count }} selected
          </div>
          <div v-else-if="new_conversation_user_id" class="of-footer-summary me-auto">
            {{ get_user_name(available_users.find(user => String(user.id) === String(new_conversation_user_id))) }}
          </div>
          <button type="button" class="btn btn-outline-secondary fw-bold px-3 py-2 rounded-3" @click="close_new_chat_modal">Cancel</button>
          <button
            v-if="new_conversation_mode === 'direct'"
            type="button"
            class="btn btn-danger fw-bold px-3 py-2 rounded-3"
            :disabled="!new_conversation_user_id || starting_conversation"
            @click="start_new_conversation"
          >
            <i v-if="starting_conversation" class="fas fa-spinner fa-spin me-2"></i>
            {{ starting_conversation ? 'Starting...' : 'Start Chat' }}
          </button>
          <button
            v-else
            type="button"
            class="btn btn-danger fw-bold px-3 py-2 rounded-3"
            :disabled="!new_group_name.trim() || !new_group_user_ids.length || starting_group"
            @click="start_new_group_conversation"
          >
            <i v-if="starting_group" class="fas fa-spinner fa-spin me-2"></i>
            {{ starting_group ? 'Creating...' : 'Create Group' }}
          </button>
        </div>
      </div>
    </div>
  </transition>

  <transition name="fc-modal">
    <div v-if="add_member_open" class="of-modal-backdrop d-flex align-items-center justify-content-center" @click.self="add_member_open = false">
      <div class="of-modal shadow-lg">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h6 class="mb-0">Add Member</h6>
          <button type="button" class="of-icon-btn subtle btn btn-light rounded-circle d-inline-flex align-items-center justify-content-center p-0" @click="add_member_open = false"><i class="fas fa-xmark"></i></button>
        </div>
        <div class="of-command-search of-chat-search-input d-flex align-items-center mb-3">
          <i class="fas fa-search"></i>
          <input v-model="user_search" type="search" placeholder="Search authorized users">
        </div>
        <button
          v-for="user in filtered_users"
          :key="`add-${user.id}`"
          type="button"
          class="of-user-result w-100 d-flex align-items-center gap-2"
          @click="add_member_to_space(user); add_member_open = false"
        >
          <span class="of-avatar d-inline-flex align-items-center justify-content-center">{{ get_initials(get_user_name(user)) }}</span>
          <span class="text-start min-w-0 flex-grow-1">
            <strong class="d-block text-truncate">{{ get_user_name(user) }}</strong>
            <small class="d-block text-truncate">{{ user.role }} - {{ user.platform }}</small>
          </span>
        </button>
      </div>
    </div>
  </transition>

  <transition name="fc-modal">
    <div v-if="remove_member_target" class="of-modal-backdrop d-flex align-items-center justify-content-center" @click.self="remove_member_target = null">
      <div class="of-modal shadow-lg">
        <button type="button" class="of-icon-btn subtle of-modal-close d-inline-flex align-items-center justify-content-center" @click="remove_member_target = null"><i class="fas fa-xmark"></i></button>
        <h6>Remove member?</h6>
        <p>Remove {{ get_user_name(remove_member_target) }} from this space draft?</p>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-light flex-fill" @click="remove_member_target = null">Cancel</button>
          <button type="button" class="btn btn-danger flex-fill" @click="remove_member">Remove</button>
        </div>
      </div>
    </div>
  </transition>

  <transition name="fc-modal">
    <div v-if="pending_delete_message" class="of-modal-backdrop d-flex align-items-center justify-content-center" @click.self="pending_delete_message = null">
      <div class="of-modal shadow-lg">
        <button type="button" class="of-icon-btn subtle of-modal-close d-inline-flex align-items-center justify-content-center" :disabled="deleting_message" @click="pending_delete_message = null"><i class="fas fa-xmark"></i></button>
        <h6>{{ pending_delete_scope === 'everyone' ? 'Delete for everyone?' : 'Delete for me?' }}</h6>
        <p class="mb-3">
          {{ pending_delete_scope === 'everyone'
            ? 'This removes the message for everyone in the conversation.'
            : 'This removes the message from your chat only. Other people will still see it.' }}
        </p>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-light flex-fill" :disabled="deleting_message" @click="pending_delete_message = null">Cancel</button>
          <button type="button" class="btn btn-danger flex-fill" :disabled="deleting_message" @click="execute_delete_message">
            <i v-if="deleting_message" class="fas fa-spinner fa-spin me-1"></i>
            {{ pending_delete_scope === 'everyone' ? 'Delete for everyone' : 'Delete for me' }}
          </button>
        </div>
      </div>
    </div>
  </transition>

  <div v-if="active_context_menu" class="of-context-menu shadow" :style="{ left: `${active_context_menu.x}px`, top: `${active_context_menu.y}px` }" @click.stop>
    <button v-if="active_context_menu.scope === 'list'" type="button" class="d-flex align-items-center gap-2" @click="apply_conversation_action('pin')"><i class="fas fa-thumbtack"></i> {{ active_context_menu.conversation?.is_pinned ? 'Unpin' : 'Pin' }}</button>
    <button type="button" class="d-flex align-items-center gap-2" @click="apply_conversation_action('mute')"><i class="fas fa-bell-slash"></i> {{ active_context_menu.conversation?.is_muted ? 'Unmute' : 'Mute' }}</button>
    <button type="button" class="d-flex align-items-center gap-2" @click="apply_conversation_action('clear')"><i class="fas fa-eraser"></i> Clear chat</button>
    <button type="button" class="danger d-flex align-items-center gap-2" @click="apply_conversation_action('delete')"><i class="fas fa-trash"></i> Delete chat</button>
  </div>

  <transition name="fc-modal">
    <div v-if="pending_conversation_action" class="of-modal-backdrop d-flex align-items-center justify-content-center" @click.self="pending_conversation_action = null">
      <div class="of-modal shadow-lg">
        <button type="button" class="of-icon-btn subtle of-modal-close d-inline-flex align-items-center justify-content-center" :disabled="conversation_action_loading" @click="pending_conversation_action = null"><i class="fas fa-xmark"></i></button>
        <h6>{{ pending_conversation_action.action === 'clear' ? 'Clear chat?' : 'Delete chat?' }}</h6>
        <p class="mb-3">
          {{ pending_conversation_action.action === 'clear'
            ? 'This clears messages for you only. Other people will still keep their copy.'
            : 'This removes the chat from your list for you only. Other people will still keep their copy.' }}
        </p>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-light flex-fill" :disabled="conversation_action_loading" @click="pending_conversation_action = null">Cancel</button>
          <button type="button" class="btn btn-danger flex-fill" :disabled="conversation_action_loading" @click="execute_conversation_action">
            <i v-if="conversation_action_loading" class="fas fa-spinner fa-spin me-1"></i>
            {{ pending_conversation_action.action === 'clear' ? 'Clear for me' : 'Delete for me' }}
          </button>
        </div>
      </div>
    </div>
  </transition>

  <button
    type="button"
    class="floating-chat-launcher upload-button d-flex align-items-center justify-content-center"
    :class="{ hovered: launcher_hovered }"
    aria-label="Open AIC One Chat"
    @mouseenter="launcher_hovered = true"
    @mouseleave="launcher_hovered = false"
    @click="toggle_open"
  >

    <i class="fa-regular fa-comment-dots"></i>
    <span v-if="total_unread" class="floating-chat-launcher-badge d-inline-flex align-items-center justify-content-center">{{ total_unread > 99 ? '99+' : total_unread }}</span>
    <span class="fc-online-indicator"></span>
    <span class="fc-launcher-tooltip shadow-sm">{{ is_open ? 'Close AIC One Chat' : 'Open AIC One Chat' }}</span>
  </button>
</div>
